import { useQuery } from '@apollo/client';
import {
  isActive,
  isAvailable,
  isCanceled,
  isIneligible,
  isPending,
  isUnavailable,
} from 'domain/product-statuses';
import gql from 'graphql-tag';

export enum ProductType {
  BillPay = 'billPay',
  ConnectedWind = 'connectedWind',
  Vnm = 'vnm',
}

export const useProductStatus = (product: ProductType, planId?: number) => {
  const { data, loading, error, refetch } = useQuery<
    GetProductStatusesQuery,
    GetProductStatusesQueryVariables
  >(GET_PRODUCT_STATUSES_QUERY, {
    skip: !planId,
    variables: {
      planId: planId || -1,
      skipWind: product !== ProductType.ConnectedWind,
      skipBillPay: product !== ProductType.BillPay,
      skipVnm: product !== ProductType.Vnm,
    },
  });

  const productStatuses = data?.plan.productStatuses || {};
  const productStatus = productStatuses[product];
  let updatedData;
  if (productStatus) {
    const { status } = productStatus;
    updatedData = {
      ...productStatus,
      isActive: isActive(status),
      isAvailable: isAvailable(status),
      isCanceled: isCanceled(status),
      isIneligible: isIneligible(status),
      isPending: isPending(status),
      isUnavailable: isUnavailable(status),
    };
  } else {
    updatedData = undefined;
  }

  return {
    data: updatedData,
    error,
    loading,
    refetch,
  };
};

export const GET_PRODUCT_STATUSES_QUERY = gql`
  query GetProductStatuses(
    $planId: Int!
    $skipWind: Boolean!
    $skipBillPay: Boolean!
    $skipVnm: Boolean!
  ) {
    plan(id: $planId) {
      id
      productStatuses {
        connectedWind @skip(if: $skipWind) {
          status
        }
        billPay @skip(if: $skipBillPay) {
          status
        }
        vnm @skip(if: $skipVnm) {
          status
        }
      }
    }
  }
`;

export const productStatusesMock = {
  plan: {
    id: 1,
    productStatuses: {
      billPay: {
        status: 'available',
      },
      connectedWind: {
        status: 'available',
      },
      vnm: {
        status: 'available',
      },
    },
  },
};

export const useProductStatusMock = {
  data: {
    isActive: true,
    isAvailable: false,
    isCanceled: false,
    isIneligible: false,
    isPending: false,
    isUnavailable: false,
    status: 'active',
  },
  error: undefined,
  loading: false,
  refetch: () => null,
};
